import { Component, Input, OnInit, signal } from '@angular/core';
import { FormControl, UntypedFormControl, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { AuthenticationMethod, Platform } from 'src/app/models/api';
import { IntegrationCredentials } from 'src/app/models/api/integration.model';
import {
  CredentialFields,
  CredentialKey,
  MultilineCredentialsKeys,
  SecretCredentialsKeys,
} from 'src/app/models/constants/credentials.constants';
import { PlatformAuthDetailsDescription } from 'src/app/models/constants/platform.constants';
import { IDetailKeyValue } from 'src/app/models/singlePageComponents';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { MsalClientService } from 'src/app/services/infra/oauthAuthorizationCodeClientService/msalClientService/msal-client-service.service';
import {
  CertificatePrivateKeyCredentialValidatorsFunctions,
  CertificateThumbprintCredentialValidatorsFunctions,
  ChatGptEnterpriseApiKeyCredentialValidatorsFunctions,
  AmazonBedrockIamRoleArnCredentialValidatorsFunctions,
  CredentialValidatorsFunctions,
  EmailCredentialValidatorsFunctions,
  GuidCredentialValidatorsFunctions,
  RequiredValidatorFunction,
  SalesforceInstanceUrlCredentialValidatorsFunctions,
  serviceNowInstanceUrlCredentialValidatorsFunctions,
} from './consts';
import { ServiceNowClientService } from 'src/app/services/infra/oauthAuthorizationCodeClientService/serviceNowClientService/serviceNow-client-service.service';
import { BehaviorSubject } from 'rxjs';
import { accountIdMismatchValidatorName } from 'src/app/common/appValidators';

@Component({
  selector: 'app-integration-credentials',
  templateUrl: './integration-credentials.component.html',
  styleUrls: ['./integration-credentials.component.scss'],
})
export class IntegrationCredentialsComponent implements OnInit {
  @Input() public platformControl: FormControl<string>;
  @Input() public authMethodControl: FormControl<string>;
  @Input() public credentialsFormGroup: UntypedFormGroup;
  @Input() public integrationCredentials: IntegrationCredentials;
  @Input() public isInAddMode: boolean;
  @Input() public formSubmitAttempt: boolean;
  @Input() public shouldUpdateCredentials: boolean;

  public svgSignInPath: string;
  public userTryingToSignInUsingOauth = false;
  public userSignedInUsingOauth = false;
  public credentialsDetails: IDetailKeyValue[];
  public formValid = false;

  public AuthenticationMethods = AuthenticationMethod;
  public MOCK_SECRET_TEXT = '●●●●●●●●●●●●●●';

  public authMethodHadChanged = false;
  private MaxSupportFileSizeInKB = 10;
  public fileWarningMessage = signal('');

  constructor(
    private readonly _msalClientService: MsalClientService,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _logger: LoggerService,
    private readonly _serviceNowClientService: ServiceNowClientService
  ) {}

  ngOnInit() {
    this.authMethodControl.valueChanges.subscribe((authMethod) => {
      this.authMethodHadChanged = true;
      this.userTryingToSignInUsingOauth = false;
      this.userSignedInUsingOauth = false;

      if (authMethod) {
        this._populateCredentialsFieldsFromPlatformAndAuthMethod(
          this.platformControl.value as Platform,
          authMethod as AuthenticationMethod
        );
      }

      this._setSvgIcon();
    });

    if (this.authMethodControl.value) {
      this._populateCredentialsFieldsFromPlatformAndAuthMethod(
        this.platformControl.value as Platform,
        this.authMethodControl.value as AuthenticationMethod
      );
      this._setSvgIcon();
    }
  }

  public signInUsingOauth() {
    this.userTryingToSignInUsingOauth = true;
    switch (this.platformControl.value as Platform) {
      case Platform.powerplatform:
        this._msalClientService.popUpMicrosoftLoginToRetrieveAuthCode().subscribe({
          next: (authCode: string) => {
            // if in add mode control already exists
            if (!this.isInAddMode) {
              this._addCredentialFormControl(Platform.powerplatform, CredentialKey.authCode);
              this.credentialsFormGroup.removeControl(CredentialKey.username);
              this._initDetails();
            }
            this.credentialsFormGroup.get(CredentialKey.authCode)?.setValue(authCode);
            this.userSignedInUsingOauth = true;
            this.userTryingToSignInUsingOauth = false;
          },
          error: (error) => {
            this._logger.error('Failed to login into Microsoft using OAuth flow, error from Microsoft: ' + error);
            this.userTryingToSignInUsingOauth = false;
          },
        });
        break;
      case Platform.serviceNow:
        this._serviceNowClientService
          .popUpServiceNowToRetrieveAuthCode(
            this.credentialsFormGroup.controls[CredentialKey.serviceNowInstanceUrl].value,
            this.credentialsFormGroup.controls[CredentialKey.clientId].value
          )
          .subscribe({
            next: (authCode: string) => {
              this.credentialsFormGroup.get(CredentialKey.authCode)?.setValue(authCode);
              this.userSignedInUsingOauth = true;
              this.userTryingToSignInUsingOauth = false;
            },
            error: (error) => {
              this._logger.error('Failed to login into ServiceNow using OAuth flow, error from ServiceNow: ' + error);
              this.userTryingToSignInUsingOauth = false;
            },
          });
        break;
    }
  }

  public onsServiceAccountJsonFileSelected(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      const fileList = event.target.files;
      if (fileList && fileList.length > 0) {
        const file = fileList[0];

        // Check file size (10KB = 10 * 1024 bytes)
        if (file.size / 1024 > this.MaxSupportFileSizeInKB) {
          this.fileWarningMessage.set('File size exceeds ' + this.MaxSupportFileSizeInKB + ' KB limit');
          this._resetFileSelection(event);
          return;
        } else {
          this.fileWarningMessage.set('');
        }

        const reader = new FileReader();
        reader.onload = () => {
          try {
            const jsonContent = JSON.parse(reader.result as string);
            if (this._validateGoogleServiceAccountJsonFile(jsonContent)) {
              this._initGooglePlatformReadOnlyCredentials();
              this.credentialsFormGroup.get(CredentialKey.clientEmail)?.setValue(jsonContent.client_email);
              this.credentialsFormGroup.get(CredentialKey.tokenUri)?.setValue(jsonContent.token_uri);
              this.credentialsFormGroup.get(CredentialKey.projectId)?.setValue(jsonContent.project_id);
              this.credentialsFormGroup.get(CredentialKey.privateKeyId)?.setValue(jsonContent.private_key_id);
              this.credentialsFormGroup.get(CredentialKey.privateKey)?.setValue(jsonContent.private_key);
              this.credentialsFormGroup.get(CredentialKey.universeDomain)?.setValue(jsonContent.universe_domain);
            }
          } catch (e) {
            this.fileWarningMessage.set('Invalid JSON file');
            this._resetFileSelection(event);
          }
        };
        reader.readAsText(file);
      }
    }
  }

  public openFileSelectionDialog(): void {
    const fileUploaderElement = document.getElementById('#ServiceAccountJsonHiddenFileUploader') as HTMLInputElement;
    if (fileUploaderElement) {
      fileUploaderElement.setAttribute('accept', '.json');
      fileUploaderElement.multiple = false; // Ensure only single file selection
      fileUploaderElement.click();
    } else {
      console.error('File uploader component is missing');
    }
  }

  private _resetFileSelection(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    element.files = null;
  }

  private _validateGoogleServiceAccountJsonFile(jsonContent: Record<string, string>): boolean {
    const requiredFields = ['client_email', 'token_uri', 'private_key'];
    let missingFields = [];

    for (const field of requiredFields) {
      if (!jsonContent[field]) {
        missingFields.push(field);
      }
    }

    if (missingFields.length > 0) {
      this.fileWarningMessage.set(`Missing required fields: ${missingFields.join(', ')}`);
      return false;
    } else {
      this.fileWarningMessage.set('');
    }

    return true;
  }

  private _addCredentialFormControl(
    platform: Platform,
    credentialKey: CredentialKey,
    includeValidators = true,
    isRequired = true
  ) {
    let validators: ValidatorFn[] = [];
    if (includeValidators) {
      validators = CredentialValidatorsFunctions;
      switch (credentialKey) {
        case CredentialKey.clientId:
          if (platform !== Platform.salesforce && platform !== Platform.serviceNow) {
            validators = GuidCredentialValidatorsFunctions;
          }
          break;
        case CredentialKey.tenantId:
          validators = GuidCredentialValidatorsFunctions;
          break;
        case CredentialKey.apiKey:
          if (platform === Platform.chatGptEnterprise) {
            validators = ChatGptEnterpriseApiKeyCredentialValidatorsFunctions;
          } else {
            validators = GuidCredentialValidatorsFunctions;
          }
          break;
        case CredentialKey.iamRoleARN:
          if (platform === Platform.amazonBedrock) {
            validators = AmazonBedrockIamRoleArnCredentialValidatorsFunctions;
          }
          break;
        case CredentialKey.accountId:
          if (platform === Platform.amazonBedrock) {
            validators = RequiredValidatorFunction;
          }
          break;
        case CredentialKey.externalId:
          if (platform === Platform.amazonBedrock) {
            validators = RequiredValidatorFunction;
          }
          break;
        case CredentialKey.email:
          validators = EmailCredentialValidatorsFunctions;
          break;
        case CredentialKey.username:
          validators = EmailCredentialValidatorsFunctions;
          break;
        case CredentialKey.certificateThumbprint:
          validators = CertificateThumbprintCredentialValidatorsFunctions;
          break;
        case CredentialKey.certificatePrivateKey:
          validators = CertificatePrivateKeyCredentialValidatorsFunctions;
          break;
        case CredentialKey.salesforceInstanceUrl:
          validators = SalesforceInstanceUrlCredentialValidatorsFunctions;
          break;
        case CredentialKey.serviceNowInstanceUrl:
          validators = serviceNowInstanceUrlCredentialValidatorsFunctions;
          break;
        case CredentialKey.workspaceId:
          validators = GuidCredentialValidatorsFunctions;
          break;
        case CredentialKey.organizationId:
          if (this.platformControl.value === Platform.google) {
            validators = RequiredValidatorFunction;
          }
          break;
        case CredentialKey.clientEmail:
          validators = EmailCredentialValidatorsFunctions;
          break;
        case CredentialKey.privateKey:
          validators = CertificatePrivateKeyCredentialValidatorsFunctions;
          break;
      }
      if (!isRequired && (this.isInAddMode || !SecretCredentialsKeys.includes(credentialKey))) {
        validators = RequiredValidatorFunction.concat(validators);
      }
    }
    let formControl = new FormControl<string>('', validators);
    if (!this.authMethodHadChanged && this.integrationCredentials && this.integrationCredentials[credentialKey]) {
      formControl = new FormControl<string>(this.integrationCredentials[credentialKey], validators);
    }
    this.credentialsFormGroup.addControl(credentialKey, formControl);
  }

  private _setSvgIcon() {
    switch (this.platformControl.value as Platform) {
      case Platform.powerplatform:
        this.svgSignInPath = 'assets/images/common/ms-sign-in.svg';
        break;
      case Platform.serviceNow:
        this.svgSignInPath = 'assets/images/common/snow-sign-in.svg';
        break;
    }
  }
  private _initDetails() {
    this.credentialsDetails = [];
    for (const credentialsKey in this.credentialsFormGroup.controls) {
      if (credentialsKey === CredentialKey.authCode) {
        continue;
      }
      const value = this.credentialsFormGroup.get(credentialsKey) as UntypedFormControl;
      this.credentialsDetails.push({
        key: this._labelsProviderPipe.transform(CredentialFields[credentialsKey]),
        formValue: value,
        isCopyable: true,
        isSensitive: SecretCredentialsKeys.includes(<CredentialKey>credentialsKey),
        isMultiline: MultilineCredentialsKeys.includes(<CredentialKey>credentialsKey),
        description: this._getCredentialFieldDescription(
          this.platformControl.value as Platform,
          this.authMethodControl.value as AuthenticationMethod,
          credentialsKey
        ),
      });
    }
  }

  private _getCredentialFieldDescription(platform: Platform, method: AuthenticationMethod, credentialKey: string) {
    try {
      return this._labelsProviderPipe.transform(PlatformAuthDetailsDescription[platform][method][credentialKey]);
    } catch {
      return '';
    }
  }
  private _populateCredentialsFieldsFromPlatformAndAuthMethod(platform: Platform, authMethod: AuthenticationMethod) {
    this.credentialsFormGroup.controls = {};
    this.credentialsFormGroup.reset();
    switch (platform) {
      case Platform.workato:
        switch (authMethod) {
          case AuthenticationMethod.apikey:
            this._addCredentialFormControl(platform, CredentialKey.email);
            this._addCredentialFormControl(platform, CredentialKey.apiKey);
            break;
          case AuthenticationMethod.userpassword:
            this._addCredentialFormControl(platform, CredentialKey.username);
            this._addCredentialFormControl(platform, CredentialKey.password);
            break;
          case AuthenticationMethod.oktasso:
            this._addCredentialFormControl(platform, CredentialKey.username);
            this._addCredentialFormControl(platform, CredentialKey.password);
            this._addCredentialFormControl(platform, CredentialKey.oktaAppEmbeddedUrl);
            this._addCredentialFormControl(platform, CredentialKey.oktaUsername);
            this._addCredentialFormControl(platform, CredentialKey.oktaPassword);
            break;
          case AuthenticationMethod.apitoken:
            this._addCredentialFormControl(platform, CredentialKey.apiToken);
        }
        break;
      case Platform.powerplatform:
        switch (authMethod) {
          case AuthenticationMethod.userpassword:
            this._addCredentialFormControl(platform, CredentialKey.tenantId);
            this._addCredentialFormControl(platform, CredentialKey.clientId);
            this._addCredentialFormControl(platform, CredentialKey.username);
            this._addCredentialFormControl(platform, CredentialKey.password);
            break;
          case AuthenticationMethod.oauthAuthorizationCode:
            if (this.isInAddMode || this.authMethodHadChanged) {
              this._addCredentialFormControl(platform, CredentialKey.authCode);
            } else {
              // if editing credentials - we want to show oauth username
              this._addCredentialFormControl(platform, CredentialKey.username, false);
            }
            break;
          case AuthenticationMethod.serviceprincipal:
            this._addCredentialFormControl(platform, CredentialKey.tenantId);
            this._addCredentialFormControl(platform, CredentialKey.clientId);
            this._addCredentialFormControl(platform, CredentialKey.servicePrincipalSecret);
            break;
          case AuthenticationMethod.ppZenityMultiTenantAppServicePrincipal:
            this._addCredentialFormControl(platform, CredentialKey.tenantId);
            this._addCredentialFormControl(platform, CredentialKey.servicePrincipalObjectId);
            break;
        }
        break;
      case Platform.powerBI:
        switch (authMethod) {
          case AuthenticationMethod.ppZenityMultiTenantAppServicePrincipal:
            this._addCredentialFormControl(platform, CredentialKey.tenantId);
            this._addCredentialFormControl(platform, CredentialKey.servicePrincipalObjectId);
            break;
        }
        break;
      case Platform.salesforce:
        this._addCredentialFormControl(platform, CredentialKey.salesforceInstanceUrl);
        this._addCredentialFormControl(platform, CredentialKey.clientId);
        this._addCredentialFormControl(platform, CredentialKey.clientSecret);
        break;
      case Platform.serviceNow:
        this._addCredentialFormControl(platform, CredentialKey.serviceNowInstanceUrl);
        this._addCredentialFormControl(platform, CredentialKey.clientId);
        this._addCredentialFormControl(platform, CredentialKey.clientSecret);
        this._addCredentialFormControl(platform, CredentialKey.authCode);
        break;
      case Platform.chatGptEnterprise:
        this._addCredentialFormControl(platform, CredentialKey.workspaceId);
        this._addCredentialFormControl(platform, CredentialKey.apiKey);
        break;
      case Platform.amazonBedrock:
        this._addCredentialFormControl(platform, CredentialKey.accountId);
        this._addCredentialFormControl(platform, CredentialKey.iamRoleARN);
        this._addCredentialFormControl(platform, CredentialKey.externalId);
        // we add these validators here so we can inspect the other controls values
        this._addAccountIdMismatchValidatorForAccountIdControl();
        this._addAccountIdMismatchValidatorForIamRoleArnControl();
        break;
      case Platform.google:
        this._addCredentialFormControl(platform, CredentialKey.organizationId);
        this._addCredentialFormControl(platform, CredentialKey.clientEmail);
        this._addCredentialFormControl(platform, CredentialKey.privateKey);
        this._addCredentialFormControl(platform, CredentialKey.tokenUri);
        this._addCredentialFormControl(platform, CredentialKey.projectId, true, false);
        this._addCredentialFormControl(platform, CredentialKey.privateKeyId, true, false);
        this._addCredentialFormControl(platform, CredentialKey.universeDomain, true, false);
        this.credentialsDetails = [];
        const credentialsKey = CredentialKey.organizationId;
        const value = this.credentialsFormGroup.get(credentialsKey) as UntypedFormControl;
        this.credentialsDetails.push({
          key: this._labelsProviderPipe.transform(CredentialFields[credentialsKey]),
          formValue: value,
          isCopyable: true,
          isSensitive: SecretCredentialsKeys.includes(<CredentialKey>credentialsKey),
          isMultiline: MultilineCredentialsKeys.includes(<CredentialKey>credentialsKey),
          description: this._getCredentialFieldDescription(
            this.platformControl.value as Platform,
            this.authMethodControl.value as AuthenticationMethod,
            credentialsKey
          ),
        });
        if (!this.isInAddMode) {
          this._initGooglePlatformReadOnlyCredentials();
        }
        return;
    }

    this._initDetails();
  }

  private _initGooglePlatformReadOnlyCredentials() {
    for (const credentialsKey in this.credentialsFormGroup.controls) {
      if (credentialsKey === CredentialKey.organizationId) {
        continue;
      }
      const value = this.credentialsFormGroup.get(credentialsKey) as UntypedFormControl;
      this.credentialsDetails.push({
        key: this._labelsProviderPipe.transform(CredentialFields[credentialsKey]),
        formValue: value,
        isCopyable: true,
        isSensitive: SecretCredentialsKeys.includes(<CredentialKey>credentialsKey),
        isMultiline: MultilineCredentialsKeys.includes(<CredentialKey>credentialsKey),
        isReadOnly: true,
        description: this._getCredentialFieldDescription(
          this.platformControl.value as Platform,
          this.authMethodControl.value as AuthenticationMethod,
          credentialsKey
        ),
      });
    }
  }

  private _addAccountIdMismatchValidatorForAccountIdControl() {
    let formControl = this.credentialsFormGroup.get(CredentialKey.accountId) as UntypedFormControl;
    // Add form group validator to check account ID matches ARN
    if (formControl) {
      const arnControl = this.credentialsFormGroup.get(CredentialKey.iamRoleARN);
      if (arnControl) {
        formControl.valueChanges.subscribe((accountId) => {
          if (!accountId || !arnControl.value) {
            return;
          }

          // Extract account ID from ARN (format: arn:aws:iam::ACCOUNT_ID:role/ROLE_NAME)
          const accountIdMatch = arnControl.value.match(/arn:aws:iam::(\d+):role\/.+/);
          const arnAccountId = accountIdMatch?.[1];

          if (arnAccountId && accountId && arnAccountId === accountId) {
            formControl.setErrors(null);
          } else {
            formControl.setErrors({ [accountIdMismatchValidatorName]: true });
          }
        });
      }
    }
  }

  private _addAccountIdMismatchValidatorForIamRoleArnControl() {
    let formControl = this.credentialsFormGroup.get(CredentialKey.iamRoleARN) as UntypedFormControl;
    if (formControl) {
      const accountIdControl = this.credentialsFormGroup.get(CredentialKey.accountId);
      if (accountIdControl) {
        formControl.valueChanges.subscribe((iamRoleArn) => {
          if (!iamRoleArn || !accountIdControl.value) {
            return;
          }

          const accountIdMatch = iamRoleArn.match(/arn:aws:iam::(\d+):role\/.+/);
          const arnAccountId = accountIdMatch?.[1];

          if (arnAccountId && accountIdControl.value && arnAccountId === accountIdControl.value) {
            formControl.setErrors(null);
            // if arnAccountId is null, we want to show the iam validation error
          } else if (arnAccountId) {
            formControl.setErrors({ [accountIdMismatchValidatorName]: true });
          }
        });
      }
    }
  }
}
